import {
  type CustomerOrderView,
  type EffectiveMenu,
  newId,
  type OrderLineInput,
  type OrderPhase,
  type OrderPrefill,
  type QuantityWarning,
} from "@kitchen/schemas";
import {
  Button,
  Chip,
  ConnectionBar,
  faDigits,
  formatDate,
  formatMoney,
  formatTime,
  formatWeekday,
  Icon,
  Initials,
  Seg,
} from "@kitchen/ui";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { api, ApiError } from "../../api.js";
import type { useHeartbeat } from "../../heartbeat.js";
import { addDays, lineKey, STATUS_LABEL, tehranToday } from "../../labels.js";
import { CustomerStatementView } from "./statement.js";
import "./customer.css";

interface Unit {
  id: string;
  name: string;
  organizationId?: string;
  organizationName: string;
}
interface Lists {
  meals: { id: string; name: string }[];
  tiers: { id: string; name: string }[];
  items: { id: string; name: string; kind: "food" | "companion" }[];
}
interface WindowInfo {
  phase: OrderPhase;
  deadlineAt: string;
  cookLockAt: string;
}
interface Target {
  unitId: string;
  date: string;
  mealId: string;
}

/** The six stages of the order path (the one bold element of design v2). */
const STAGES = [
  {
    label: "ثبت شد",
    reached: [
      "submitted",
      "approved",
      "in-production",
      "ready",
      "assigned",
      "on-route",
      "delivered",
      "delivered-short",
    ],
  },
  {
    label: "تأیید مدیر",
    reached: ["approved", "in-production", "ready", "assigned", "on-route", "delivered", "delivered-short"],
  },
  {
    label: "در دست پخت",
    reached: ["in-production", "ready", "assigned", "on-route", "delivered", "delivered-short"],
  },
  { label: "بسته‌بندی", reached: ["ready", "assigned", "on-route", "delivered", "delivered-short"] },
  { label: "با راننده", reached: ["assigned", "on-route", "delivered", "delivered-short"] },
  { label: "تحویل", reached: ["delivered", "delivered-short"] },
];

/**
 * F3-T14: the customer door (design v2 CustomerHome.html / CustomerOrder.html). Today and tomorrow per meal,
 * the order path, the time left, the order form, and a polite change request after the cook lock (DL-010/011).
 * Prices appear only when the server sends them (seePrices, DL-039).
 */
export function CustomerHome({
  name,
  onLock,
  hb,
}: {
  name: string;
  onLock: () => void;
  hb: ReturnType<typeof useHeartbeat>;
}) {
  const units = useQuery({
    queryKey: ["my-units"],
    queryFn: () => api<Unit[]>("/my/units").catch(() => [] as Unit[]),
  });
  const lists = useQuery({ queryKey: ["my-lists"], queryFn: () => api<Lists>("/my/menu/lists") });
  const [unitId, setUnitId] = useState<string | null>(null);
  const [target, setTarget] = useState<Target | null>(null);
  const [tab, setTab] = useState<"orders" | "statement">("orders");
  const unit = unitId ?? units.data?.[0]?.id ?? null;
  const today = tehranToday();

  const header = (
    <header className="appbar">
      <Initials text={name.slice(0, 2)} tone="c" />
      <div className="grow">
        <div className="m">{name}</div>
        {unit && (
          <div className="t-cap muted">
            {faDigits(units.data?.find((u) => u.id === unit)?.organizationName ?? "")}
          </div>
        )}
      </div>
      <ConnectionBar state={hb.state} lastOkAt={hb.lastOkAt} now={hb.now} />
      <Button variant="ghost" icon="lock" onClick={onLock} aria-label="قفل">
        قفل
      </Button>
    </header>
  );

  if (target && lists.data) {
    return (
      <main className="device">
        {header}
        <OrderForm
          target={target}
          lists={lists.data}
          onDone={() => {
            setTarget(null);
          }}
        />
      </main>
    );
  }

  return (
    <main className="device">
      {header}
      <div className="cust-wrap">
        {units.data && units.data.length > 0 && (
          <div style={{ marginBottom: "var(--s3)" }}>
            <Seg<"orders" | "statement">
              label="بخش پرتال مشتری"
              value={tab}
              onChange={setTab}
              options={[
                { value: "orders", label: "سفارش غذا" },
                { value: "statement", label: "صورتحساب و فاکتورها" },
              ]}
            />
          </div>
        )}

        {tab === "statement" && unit && (
          <CustomerStatementView
            organizationId={units.data?.find((u) => u.id === unit)?.organizationId ?? ""}
            organizationName={units.data?.find((u) => u.id === unit)?.organizationName ?? "سازمان شما"}
          />
        )}

        {tab === "orders" && (
          <>
            <h1 className="t-page">واحد(های) من</h1>
            {units.data && units.data.length === 1 && (
              <div className="card">
                <div className="m">{faDigits(units.data[0]?.name ?? "")}</div>
                <div className="t-cap muted">{faDigits(units.data[0]?.organizationName ?? "")}</div>
              </div>
            )}
            {units.data && units.data.length === 0 && (
              <div className="card">
                <p className="ink2">
                  هنوز به واحدی وصل نیستید. اگر رابط سفارش یک واحد هستید، با آشپزخانه تماس بگیرید تا شماره‌ی
                  شما ثبت شود.
                </p>
              </div>
            )}
            {units.data && units.data.length > 1 && unit && (
              <Seg
                label="واحد"
                value={unit}
                onChange={setUnitId}
                options={units.data.map((u) => ({ value: u.id, label: faDigits(u.name) }))}
              />
            )}
            {unit && lists.data && (
              <>
                {[today, addDays(today, 1)].map((date) => (
                  <section key={date} className="stack">
                    <h2 className="t-title">
                      {date === today ? "امروز" : "فردا"} · {formatWeekday(date)} {formatDate(date)}
                    </h2>
                    {lists.data.meals.map((m) => (
                      <MealCard key={m.id} unitId={unit} date={date} meal={m} onOpen={setTarget} />
                    ))}
                  </section>
                ))}
                <Recent unitId={unit} lists={lists.data} />
              </>
            )}
          </>
        )}
      </div>
    </main>
  );
}

function useWindow(t: Target) {
  return useQuery({
    queryKey: ["my-window", t.unitId, t.date, t.mealId],
    queryFn: () => api<WindowInfo>(`/my/order-window?unitId=${t.unitId}&date=${t.date}&mealId=${t.mealId}`),
    refetchInterval: 60_000,
  });
}

function useMyOrder(t: Target) {
  return useQuery({
    queryKey: ["my-orders", t.date],
    queryFn: () => api<CustomerOrderView[]>(`/my/orders?from=${t.date}&to=${t.date}`),
    select: (all) => all.find((o) => o.orderingUnitId === t.unitId && o.mealId === t.mealId) ?? null,
  });
}

function Countdown({ w }: { w: WindowInfo }) {
  const ms = new Date(w.deadlineAt).getTime() - Date.now();
  if (w.phase === "open") {
    const min = Math.max(0, Math.round(ms / 60_000));
    const text = min >= 120 ? `${faDigits(Math.floor(min / 60))} ساعت` : `${faDigits(min)} دقیقه`;
    return (
      <div className="cd-box">
        <span className="row" style={{ gap: "var(--s2)" }}>
          <Icon name="clock" small />
          مهلت ثبت تا {formatTime(w.deadlineAt)}
        </span>
        <span className="b">{text} مانده</span>
      </div>
    );
  }
  if (w.phase === "late") {
    return (
      <div className="cd-box" style={{ background: "var(--warn-soft)", color: "var(--warn-ink)" }}>
        <span>مهلت ثبت گذشته است؛ تا {formatTime(w.cookLockAt)} هنوز می‌توانید ویرایش کنید.</span>
      </div>
    );
  }
  return null;
}

function Journey({ status }: { status: CustomerOrderView["status"] }) {
  const reachedCount = STAGES.filter((s) => s.reached.includes(status)).length;
  return (
    <div className="journey-mobile" aria-label={`وضعیت: ${STATUS_LABEL[status]}`}>
      {STAGES.map((s, i) => {
        const done = s.reached.includes(status);
        const active = !done && i === reachedCount;
        return (
          <div key={s.label} className={`j-step${done ? " done" : ""}${active ? " active" : ""}`}>
            <div className="j-dot">{done ? <Icon name="check" small /> : faDigits(i + 1)}</div>
            <span className="t-cap b">{s.label}</span>
          </div>
        );
      })}
    </div>
  );
}

function MealCard({
  unitId,
  date,
  meal,
  onOpen,
}: {
  unitId: string;
  date: string;
  meal: { id: string; name: string };
  onOpen: (t: Target) => void;
}) {
  const t = { unitId, date, mealId: meal.id };
  const w = useWindow(t);
  const order = useMyOrder(t);
  const o = order.data;
  const portions = o?.lines.filter((l) => l.tierId !== null).reduce((n, l) => n + l.orderedQuantity, 0) ?? 0;
  const label = !o
    ? w.data?.phase === "locked"
      ? "ثبت سفارش (بعد از قفل پخت)"
      : "ثبت سفارش"
    : w.data?.phase === "locked" || !["submitted", "returned", "approved"].includes(o.status)
      ? "مشاهده و درخواست تغییر"
      : "مشاهده و ویرایش";
  return (
    <div className="card meal-card">
      <div className="row-between">
        <div>
          <div className="t-lead">{meal.name}</div>
          {o ? (
            <div className="t-cap muted">
              {faDigits(portions)} پرس · {STATUS_LABEL[o.status]}
              {o.openChangeRequest && " · درخواست تغییر در حال بررسی"}
            </div>
          ) : (
            <div className="t-cap muted">هنوز ثبت نشده</div>
          )}
        </div>
        {o?.status === "returned" && <Chip tone="coral">برگشت برای اصلاح</Chip>}
      </div>
      {o?.status === "returned" && o.returnNote && <p className="ink2">پیام آشپزخانه: «{o.returnNote}»</p>}
      {o && o.status !== "cancelled" && <Journey status={o.status} />}
      {w.data && <Countdown w={w.data} />}
      {o && o.lines.some((l) => l.unitAmount !== undefined) && (
        <div className="row-between t-cap">
          <span className="muted">مبلغ</span>
          <span className="b">
            {formatMoney(o.lines.reduce((s, l) => s + (l.unitAmount ?? 0) * l.orderedQuantity, 0))}
          </span>
        </div>
      )}
      <Button
        variant={o ? "default" : "primary"}
        size="lg"
        block
        onClick={() => {
          onOpen(t);
        }}
      >
        {label}
      </Button>
    </div>
  );
}

function Recent({ unitId, lists }: { unitId: string; lists: Lists }) {
  const today = tehranToday();
  const recent = useQuery({
    queryKey: ["my-orders-recent", today],
    queryFn: () =>
      api<CustomerOrderView[]>(`/my/orders?from=${addDays(today, -14)}&to=${addDays(today, -1)}`),
  });
  const rows = (recent.data ?? []).filter((o) => o.orderingUnitId === unitId).slice(0, 8);
  if (!rows.length) return null;
  const meal = (id: string) => lists.meals.find((m) => m.id === id)?.name ?? "";
  return (
    <section className="stack">
      <h2 className="t-title">سفارش‌های اخیر</h2>
      <div className="card items">
        {rows.map((o) => (
          <div key={o.id} className="item">
            <div className="grow">
              <div className="m">
                {formatWeekday(o.businessDate)} {formatDate(o.businessDate)} · {meal(o.mealId)}
              </div>
              <div className="t-cap muted">{STATUS_LABEL[o.status]}</div>
            </div>
            <span className="num b">
              {faDigits(o.lines.filter((l) => l.tierId !== null).reduce((n, l) => n + l.orderedQuantity, 0))}
            </span>
          </div>
        ))}
      </div>
    </section>
  );
}

function Stepper({
  label,
  value,
  warn,
  onChange,
}: {
  label: string;
  value: number;
  warn?: boolean;
  onChange: (n: number) => void;
}) {
  return (
    <span className={`stepper${warn ? " warn" : ""}`}>
      <button
        type="button"
        aria-label={`کم کردن ${label}`}
        onClick={() => {
          onChange(Math.max(0, value - 1));
        }}
      >
        <Icon name="minus" small />
      </button>
      <output className="num" aria-label={label}>
        <input
          className="num"
          inputMode="numeric"
          aria-label={`تعداد ${label}`}
          value={faDigits(value)}
          onChange={(e) => {
            const n = Number(
              e.target.value.replace(/[۰-۹]/g, (d) => String("۰۱۲۳۴۵۶۷۸۹".indexOf(d))).replace(/\D/g, "") ||
                "0",
            );
            onChange(Math.min(10_000, n));
          }}
          style={{ width: 56, textAlign: "center", border: 0, background: "transparent", font: "inherit" }}
        />
      </output>
      <button
        type="button"
        aria-label={`زیاد کردن ${label}`}
        onClick={() => {
          onChange(Math.min(10_000, value + 1));
        }}
      >
        <Icon name="plus" small />
      </button>
    </span>
  );
}

function OrderForm({ target, lists, onDone }: { target: Target; lists: Lists; onDone: () => void }) {
  const qc = useQueryClient();
  const w = useWindow(target);
  const order = useMyOrder(target);
  const menu = useQuery({
    queryKey: ["my-menu", target.unitId, target.date, target.mealId],
    queryFn: () =>
      api<EffectiveMenu>(`/my/menu?unitId=${target.unitId}&date=${target.date}&mealId=${target.mealId}`),
  });
  const prefill = useQuery({
    queryKey: ["my-prefill", target.unitId, target.date, target.mealId],
    queryFn: () =>
      api<OrderPrefill>(
        `/my/orders-prefill?unitId=${target.unitId}&date=${target.date}&mealId=${target.mealId}`,
      ),
    enabled: order.isSuccess && order.data === null,
  });
  const [counts, setCounts] = useState<Record<string, number> | null>(null);
  const [note, setNote] = useState("");
  const [warnings, setWarnings] = useState<QuantityWarning[] | null>(null);
  const [message, setMessage] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const o = order.data;
  const phase = w.data?.phase;
  const changeOnly =
    Boolean(o) && (phase === "locked" || !["submitted", "returned", "approved"].includes(o?.status ?? ""));
  const pendingChange = o?.openChangeRequest ?? false;

  // Starting counts: the existing order, else the prefill (template / same weekday / last order).
  const base: Record<string, number> = {};
  for (const l of o?.lines ?? prefill.data?.lines.map((p) => ({ ...p, orderedQuantity: p.quantity })) ?? []) {
    base[lineKey(l)] = l.orderedQuantity;
  }
  const value = (k: string) => counts?.[k] ?? base[k] ?? 0;
  const tierName = (id: string) => lists.tiers.find((t) => t.id === id)?.name ?? "";
  const mealName = lists.meals.find((m) => m.id === target.mealId)?.name ?? "";

  const rows = (menu.data?.items ?? []).flatMap((item) =>
    item.tierIds.length
      ? item.tierIds.map((tierId) => ({
          key: lineKey({ menuItemId: item.menuItemId, tierId }),
          item,
          tierId: tierId,
        }))
      : [
          {
            key: lineKey({ menuItemId: item.menuItemId, tierId: null }),
            item,
            tierId: null as string | null,
          },
        ],
  );
  // Lines of the order that are not on today's menu (extra lines) stay as they are.
  const extras = (o?.lines ?? []).filter((l) => !rows.some((r) => r.key === lineKey(l)));
  const lines: OrderLineInput[] = [
    ...rows
      .filter((r) => value(r.key) > 0)
      .map((r) => ({
        menuItemId: r.item.menuItemId,
        tierId: r.tierId,
        quantity: value(r.key),
        note: "",
        extra: false,
      })),
    ...extras.map((l) => ({
      menuItemId: l.menuItemId,
      tierId: l.tierId,
      quantity: value(lineKey(l)) || l.orderedQuantity,
      note: l.note,
      extra: true,
    })),
  ];
  const portions = lines.filter((l) => l.tierId !== null).reduce((n, l) => n + l.quantity, 0);
  const warnFor = (k: string) => warnings?.find((x) => lineKey(x) === k);

  async function submit(confirmed: boolean) {
    setBusy(true);
    setMessage(null);
    try {
      if (changeOnly && o) {
        await api(`/my/orders/${o.id}/change-requests`, {
          body: { id: newId(), orderVersion: o.version, lines, note },
        });
        setMessage("درخواست شما ثبت شد و برای هماهنگی ارسال گردید. سفارش فعلی تا پاسخ آشپزخانه معتبر است.");
      } else {
        await api("/my/orders", {
          body: {
            id: o?.id ?? newId(),
            orderingUnitId: target.unitId,
            businessDate: target.date,
            mealId: target.mealId,
            lines,
            contactNote: note || (o?.contactNote ?? ""),
            ...(o ? { version: o.version } : {}),
            confirmedWarnings: confirmed,
          },
        });
        setMessage("سفارش شما ثبت شد و پس از تأیید آشپزخانه آماده می‌شود.");
      }
      setWarnings(null);
      await qc.invalidateQueries({ queryKey: ["my-orders"] });
      await qc.invalidateQueries({ queryKey: ["my-window"] });
    } catch (err) {
      if (err instanceof ApiError && err.code === "confirm-quantities") {
        setWarnings((err.body as { warnings?: QuantityWarning[] } | null)?.warnings ?? []);
        setMessage(err.message);
      } else if (
        err instanceof ApiError &&
        (err.code === "exists" || err.code === "version-conflict" || err.code === "locked")
      ) {
        await qc.invalidateQueries({ queryKey: ["my-orders"] });
        await qc.invalidateQueries({ queryKey: ["my-window"] });
        setMessage(err.message);
      } else {
        setMessage(err instanceof ApiError ? err.message : "ارتباط برقرار نشد؛ دوباره امتحان کنید");
      }
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="cust-wrap">
      <div className="row" style={{ gap: "var(--s2)" }}>
        <Button variant="ghost" icon="right" onClick={onDone}>
          بازگشت
        </Button>
      </div>
      <h1 className="t-page">
        سفارش {mealName} — {formatWeekday(target.date)} {formatDate(target.date)}
      </h1>
      {w.data && <Countdown w={w.data} />}
      {changeOnly && (
        <div className="softlock-banner" role="status">
          <div className="b">زمان سفارشات تمام شده است؛ مایل به تغییر هستید؟</div>
          <div>
            سفارش فعلی شما معتبر است و آماده می‌شود. اگر تغییری لازم است، درخواست بدهید تا آشپزخانه بررسی کند.
          </div>
        </div>
      )}
      {!o && phase === "locked" && (
        <div className="softlock-banner" role="status">
          <div>زمان معمول سفارش گذشته است؛ سفارش شما ثبت و فوراً به مدیر آشپزخانه اطلاع داده می‌شود.</div>
        </div>
      )}
      {pendingChange && <Chip tone="warn">درخواست تغییر قبلی شما در حال بررسی است</Chip>}
      {!o && prefill.data && prefill.data.source !== "none" && (
        <p className="t-cap muted">
          {prefill.data.source === "template"
            ? "از الگوی همیشگی شما پر شد"
            : prefill.data.source === "same-weekday"
              ? "مثل همین روز هفته‌ی قبل پر شد"
              : "مثل آخرین سفارش شما پر شد"}
          ؛ اعداد را بررسی کنید.
        </p>
      )}

      <div className="card">
        {menu.isLoading && <p className="muted">در حال بارگیری منو…</p>}
        {menu.data && rows.length === 0 && <p className="muted">برای این وعده هنوز منویی ثبت نشده است.</p>}
        {rows.map((r) => {
          const label = `${r.item.name}${r.tierId ? ` ${tierName(r.tierId)}` : ""}`;
          const wn = warnFor(r.key);
          return (
            <div key={r.key} className="tier-row">
              <div>
                <div className="m">{r.item.name}</div>
                {r.tierId && <Chip plain>{tierName(r.tierId)}</Chip>}
                {wn && (
                  <div className="t-cap" style={{ color: "var(--warn-ink)" }}>
                    معمولاً {faDigits(wn.usual)}؛ مطمئنید؟
                  </div>
                )}
              </div>
              <Stepper
                label={label}
                value={value(r.key)}
                warn={Boolean(wn)}
                onChange={(n) => {
                  setCounts({ ...(counts ?? base), [r.key]: n });
                }}
              />
            </div>
          );
        })}
      </div>

      <div className="field">
        <label htmlFor="cust-note">
          {changeOnly ? "توضیح درخواست" : "یادداشت برای آشپزخانه (مثلاً «کم‌برنج»)"}
        </label>
        <textarea
          id="cust-note"
          className="input cust-note"
          value={note}
          onChange={(e) => {
            setNote(e.target.value);
          }}
        />
      </div>

      <div className="row-between">
        <span className="muted">جمع</span>
        <span className="b">{faDigits(portions)} پرس</span>
      </div>
      {message && (
        <p className="ink2" role="status">
          {message}
        </p>
      )}
      {warnings ? (
        <div className="row" style={{ gap: "var(--s2)" }}>
          <Button
            variant="primary"
            size="lg"
            disabled={busy}
            onClick={() => {
              void submit(true);
            }}
          >
            بله، درست است
          </Button>
          <Button
            variant="ghost"
            onClick={() => {
              setWarnings(null);
              setMessage(null);
            }}
          >
            اصلاح می‌کنم
          </Button>
        </div>
      ) : (
        <Button
          variant="primary"
          size="lg"
          block
          disabled={busy || portions === 0 || (changeOnly && pendingChange)}
          onClick={() => {
            void submit(false);
          }}
        >
          {changeOnly ? "ارسال درخواست تغییر" : o ? "ثبت تغییرات" : "ثبت سفارش"}
        </Button>
      )}
    </div>
  );
}
